import * as React from "react";
import { Alert, Box, Chip, CircularProgress, LinearProgress, Link, Paper, Typography } from "@mui/material";
import { convertMessageToHtml } from "../../../utilities/helpers";
import { MESSAGE_STYLES } from "../../../utilities/constants";
import { pairKey } from "./chatHistory";

function References({ turn, onDownload }) {
    const allCitations = turn.citations.split("\n").filter((citation) => citation.trim() !== "");
    // citedCount leading entries carry a matching [n] marker in the answer text;
    // the rest were added only to meet the minimum citation count.
    const citedCount = Math.min(turn.citedCount ?? allCitations.length, allCitations.length);
    const cited = allCitations.slice(0, citedCount);
    const additional = allCitations.slice(citedCount);

    return (
        <Box sx={{ mt: 1 }}>
            {cited.length > 0 && (
                <Paper>
                    <Typography variant="h6" sx={{ pt: 1.25, px: 1.25 }}>References:</Typography>
                    <Box sx={{ p: 2, lineHeight: 1.5 }}>
                        {cited.map((citation, index) => (
                            <div key={index}>
                                <Link href="#" onClick={() => onDownload(turn.commodity, turn.market, citation)}>[{index + 1}] {citation}</Link>
                            </div>
                        ))}
                    </Box>
                </Paper>
            )}
            {additional.length > 0 && (
                <Paper sx={{ mt: cited.length > 0 ? 1 : 0 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1.25, px: 1.25 }}>
                        Additional related documents (found by search, not directly cited above):
                    </Typography>
                    <Box sx={{ p: 2, lineHeight: 1.5 }}>
                        {additional.map((citation, index) => (
                            <div key={index}>
                                <Link href="#" onClick={() => onDownload(turn.commodity, turn.market, citation)}>{citation}</Link>
                            </div>
                        ))}
                    </Box>
                </Paper>
            )}
        </Box>
    );
}

function StreamingStatus({ status }) {
    const label = status?.status === "uploading_file"
        ? "Uploading file..."
        : status?.status === "streaming"
            ? "Generating response..."
            : "Searching documents...";

    return (
        <Box sx={{ mb: 1 }}>
            <LinearProgress variant="indeterminate" sx={{ mb: 0.5 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <CircularProgress size={12} thickness={5} />
                <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
        </Box>
    );
}

function Turn({ turn, status, onDownload }) {
    return (
        <Box sx={{ mb: 3 }}>
            {turn.commodity && turn.market && (
                <Chip label={`${turn.commodity} - ${turn.market}`} color="primary" variant="outlined" size="small" sx={{ mb: 1 }} />
            )}

            {turn.question && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <Box
                        sx={{
                            maxWidth: "85%",
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                            borderRadius: "18px",
                            px: 2,
                            py: 1.25,
                            fontSize: "0.95rem",
                            lineHeight: 1.5,
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {turn.question}
                    </Box>
                </Box>
            )}

            {turn.errorMessage && <Alert severity="warning" sx={{ mb: 1 }}>{turn.errorMessage}</Alert>}
            {turn.isStreaming && <StreamingStatus status={status} />}

            {turn.message && (
                <Paper>
                    <Box
                        dangerouslySetInnerHTML={{ __html: convertMessageToHtml(turn.message) }}
                        sx={{
                            p: 2,
                            fontSize: "0.95rem",
                            lineHeight: 1.55,
                            wordBreak: "break-word",
                            "& h1, & h2, & h3, & h4, & h5, & h6": { fontWeight: 600, lineHeight: 1.3, margin: "16px 0 6px" },
                            "& > :first-of-type": { marginTop: 0 },
                            "& strong": { fontWeight: 600 },
                            "& em": { fontStyle: "italic" },
                            "& pre": { ...MESSAGE_STYLES.pre, p: 1.5, borderRadius: "6px", margin: "8px 0" },
                            "& code": MESSAGE_STYLES.code,
                        }}
                    />
                    {turn.elapsed != null && (
                        <Box sx={{ px: 2, pb: 1.5, display: "flex", flexWrap: "wrap", gap: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Total: {turn.elapsed.toFixed(1)}s
                            </Typography>
                            {turn.cancelled && (
                                <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>Cancelled</Typography>
                            )}
                        </Box>
                    )}
                </Paper>
            )}

            {turn.citations && <References turn={turn} onDownload={onDownload} />}
        </Box>
    );
}

export default function ChatMessages({ turns, streamingStatus, onDownload }) {
    const endRef = React.useRef(null);

    React.useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [turns]);

    return (
        <Box>
            {turns.map((turn) => (
                <Turn
                    key={turn.id}
                    turn={turn}
                    status={streamingStatus[pairKey(turn.commodity, turn.market)]}
                    onDownload={onDownload}
                />
            ))}
            <div ref={endRef} />
        </Box>
    );
}
